import axios from "axios";
import { config } from '../../config/Config';

export const registerNewGame = async (obj) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${config.serverBaseUrl}/v1/web/game-mgmt`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`
            },
            data: obj.gameData
        });
        return response.data;
    } catch (error) {
        console.error('Error registering new game:', error.response?.data || error.message);
        throw error;
    }
};

export const getGameRegistries = async (obj) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/game-mgmt`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching game registries:', error.response?.data || error.message);
        throw error;
    }
};

export const getGameRegistriesByOrgId = async (obj) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/game-mgmt/orgId/${obj.orgId}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching game registries by org ID:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Every game in the org, each tagged with whether the calling user can
 * already access it. Used by the header context switcher so users can see
 * the full org catalogue and request access to games they don't yet have.
 *
 * Returns { games: [{ game: GameRegistryDTO, accessible: boolean }] }.
 */
export const getGameRegistriesByOrgIdWithAccessibility = async ({ orgId, accessToken }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/game-mgmt/orgId/${orgId}/with-accessibility`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching org game registries with accessibility:', error.response?.data || error.message);
        throw error;
    }
};

export const getGameRegistryById = async (obj) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/game-mgmt/details`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`,
                // The by-id route became GET /details, reading the game from X-Web-Game-Id (MDC).
                // The old /id/{id} path no longer exists.
                'X-Web-Game-Id': obj.id
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching game registry by ID:', error.response?.data || error.message);
        throw error;
    }
};

export const getGameRegistryByGameClientId = async (obj) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/game-mgmt/game-id/${obj.gameClientId}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching game registry by game client ID:', error.response?.data || error.message);
        throw error;
    }
};

// Minimal, auth-only lifecycle lookups (gameState + clientState) powering the scope-lifecycle UI
// guard. Unlike the full game-registry reads above, these are NOT gated by the games-page READ
// permission, so every game member (incl. Developers) can resolve whether their scope is frozen.
// The scope's lifecycle, for the UI freeze guard. GameRegistryApi exposes this at both
// /lifecycle/game/{id} and /lifecycle/game-id/{id} — two routes left over from the game-client
// cutover that now take the same id and return the same thing, `clientState` always null. One
// caller, one route.
export const getScopeLifecycleByGameId = async ({ gameId, accessToken }) => {
    const response = await axios({
        method: 'get',
        url: `${config.serverBaseUrl}/v1/web/game-mgmt/lifecycle/game/${gameId}`,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` }
    });
    return response.data; // { gameState, clientState: null }
};

// ── STALE: the legacy game-form surface ───────────────────────────────────────────────────────────
// The four calls below still address the pre-header-scoping API — `PUT /game-mgmt/{reg}/{client}`
// and `/{reg}/{client}/secrets…` — which no longer have handlers (the routes are `PUT ""` and a
// game-scoped `/secrets`, keyed on X-Web-Game-Id). They are NOT repointed here on purpose: their
// only callers are GameRegistrationForm and ClientSecretManager (via BuildClientSecret /
// GameClientEditForm / GameRegistrationFormMain / GameSetup), all of which are still built around
// per-client "build" rows — a level the model no longer has. Fixing the URL alone would turn a
// visible failure into a save that appears to succeed while sending a client-shaped payload.
//
// They belong to one piece of work: de-legacy that form surface and move it onto the game-scoped
// services (services/games/GameClientSecretService.jsx already has correct rotate/revoke/get).
// Until then Game Management's Edit deep-links to /game/<slug>/settings/details instead.
// ─────────────────────────────────────────────────────────────────────────────────────────────────

export const updateGameRegistry = async (obj) => {
    try {
        const response = await axios({
            method: 'put',
            url: `${config.serverBaseUrl}/v1/web/game-mgmt/${obj.gameRegistryId}/${obj.gameClientId}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`
            },
            data: obj.gameData
        });
        return response.data;
    } catch (error) {
        console.error('Error updating game registry:', error.response?.data || error.message);
        throw error;
    }
};

export const getGameSecretKeys = async ({ accessToken, gameRegistryId, gameClientId }) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/game-mgmt/${gameRegistryId}/${gameClientId}/secrets`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching game secret keys:', error.response?.data || error.message);
        throw error;
    }
};

export const revokeGameSecret = async ({ accessToken, gameRegistryId, gameClientId, keyId }) => {
    try {
        const response = await axios({
            method: 'delete',
            url: `${config.serverBaseUrl}/v1/web/game-mgmt/${gameRegistryId}/${gameClientId}/secrets/${keyId}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error revoking game secret:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Mint a fresh keyId + secret pair without persisting. Used by the
 * registration form to show the secret inline next to each build's
 * Client ID at registration time — secrets are never returned by any
 * read API, so this is the only chance the operator gets to view the
 * raw value. The same secret is then submitted back inside the
 * build's `clientSecret` field when the form saves; the backend
 * persists it verbatim instead of generating a second one.
 *
 * Returns: { keyId, secret, lastModifiedBy, lastModifiedDate }
 */
export const generateClientSecret = async ({ accessToken } = {}) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${config.serverBaseUrl}/v1/web/game-mgmt/generate-secret`,
            headers: {
                'Content-Type': 'application/json',
                ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error generating client secret:', error.response?.data || error.message);
        throw error;
    }
};

// `notify` (default true) controls the rotation email. Pass notify=false for first-time key issuance
// (onboarding / setup) where the flow rotates-then-revokes — emailing there announces a misleading
// "2 active secrets" for what is really the first secret. Genuine user rotations leave it true.
export const rotateGameSecret = async ({ accessToken, gameRegistryId, gameClientId, notify = true }) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${config.serverBaseUrl}/v1/web/game-mgmt/${gameRegistryId}/${gameClientId}/rotate-secret`,
            params: { notify },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            data: {}
        });
        return response.data;
    } catch (error) {
        console.error('Error rotating game secret:', error.response?.data || error.message);
        throw error;
    }
};